import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthContext } from '@/providers/AuthProvider';
import { isPathAllowedByMenus, menusOf } from '@/lib/menuAccess';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Calendar,
  CornerDownLeft,
  LayoutDashboard,
  Megaphone,
  Package,
  RefreshCw,
  Search,
  ShoppingBag,
  Ticket,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Command {
  id: string;
  label: string;
  description: string;
  keywords: string[];
  shortcut?: string;
  to: string;
  icon: LucideIcon;
  group: 'nav' | 'create' | 'action';
}

const ALL_COMMANDS: ReadonlyArray<Command> = [
  // Navigation (G + key)
  { id: 'nav-dashboard', label: '대시보드로 이동', description: '운영 현황 및 KPI', keywords: ['dashboard', 'home', '대시보드'], shortcut: 'G D', to: '/dashboard', icon: LayoutDashboard, group: 'nav' },
  { id: 'nav-orders', label: '주문 관리', description: '주문 목록 및 상태 관리', keywords: ['orders', '주문'], shortcut: 'G O', to: '/orders', icon: ShoppingBag, group: 'nav' },
  { id: 'nav-products', label: '상품 관리', description: '카탈로그 + SKU 관리', keywords: ['products', 'catalog', '상품'], shortcut: 'G P', to: '/products', icon: Package, group: 'nav' },
  { id: 'nav-users', label: '회원 관리', description: '고객 + 등급 + 마스킹', keywords: ['users', 'members', '회원'], shortcut: 'G U', to: '/users/members', icon: Users, group: 'nav' },
  { id: 'nav-coupons', label: '쿠폰 관리', description: '발행/사용 이력', keywords: ['coupons', '쿠폰'], shortcut: 'G C', to: '/coupons', icon: Ticket, group: 'nav' },
  { id: 'nav-events', label: '이벤트 관리', description: '응모 이벤트', keywords: ['events', '이벤트'], shortcut: 'G E', to: '/cms/events', icon: Calendar, group: 'nav' },
  { id: 'nav-notices', label: '공지사항', description: 'CMS 공지 + 배너', keywords: ['cms', 'notice', '공지'], shortcut: 'G N', to: '/cms/popups', icon: Megaphone, group: 'nav' },
  { id: 'nav-claims', label: '클레임', description: '환불/교환 처리 대기', keywords: ['claims', '환불', '클레임'], shortcut: 'G L', to: '/claims', icon: RefreshCw, group: 'nav' },

  // Create
  { id: 'create-product', label: '새 상품 등록', description: '카탈로그 상품 추가', keywords: ['new product', 'create', '신규 상품'], to: '/products/new', icon: Package, group: 'create' },
  { id: 'create-coupon', label: '새 쿠폰 발행', description: '프로모션 쿠폰', keywords: ['new coupon', '신규 쿠폰'], to: '/coupons', icon: Ticket, group: 'create' },
  { id: 'create-event', label: '새 이벤트', description: '응모 이벤트 생성', keywords: ['new event', '신규 이벤트'], to: '/cms/events/new', icon: Calendar, group: 'create' },
  { id: 'create-notice', label: '새 공지사항', description: 'CMS 공지 작성', keywords: ['new notice', '신규 공지'], to: '/cms/notices/new', icon: Megaphone, group: 'create' },

  // Action
  { id: 'action-pending-claims', label: '클레임 대기 검토', description: '접수 상태 필터', keywords: ['claims pending', '클레임 대기'], to: '/claims?claimStatus=REQUESTED', icon: RefreshCw, group: 'action' },
  { id: 'action-cancel-requests', label: '취소 요청 검토', description: '주문 취소 대기', keywords: ['orders cancel', '취소'], to: '/orders?orderStatus=CANCEL_REQUESTED', icon: ShoppingBag, group: 'action' },
];

const GROUP_LABEL: Record<Command['group'], string> = {
  nav: '이동',
  create: '새로 만들기',
  action: '바로가기',
};

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CommandPalette = ({ open, onOpenChange }: CommandPaletteProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // QA-514 역할별 접근 메뉴 — 숨긴 메뉴(와 그 아래 화면)로 가는 명령은 빼낸다.
  const menus = menusOf(useAuthContext().currentAdmin);
  const menuKey = JSON.stringify(menus);

  // Filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const allowed = ALL_COMMANDS.filter((c) => isPathAllowedByMenus(JSON.parse(menuKey) as string[] | null, c.to));
    if (!q) return allowed;
    return allowed.filter((c) => {
      if (c.label.toLowerCase().includes(q)) return true;
      if (c.description.toLowerCase().includes(q)) return true;
      return c.keywords.some((kw) => kw.toLowerCase().includes(q));
    });
  }, [query, menuKey]);

  // Group
  const grouped = useMemo(() => {
    const groups: Record<Command['group'], Command[]> = { nav: [], create: [], action: [] };
    filtered.forEach((c) => groups[c.group].push(c));
    return groups;
  }, [filtered]);

  // Flat sorted (used for keyboard navigation index mapping)
  const flatOrder = useMemo(
    () => [...grouped.nav, ...grouped.create, ...grouped.action],
    [grouped],
  );

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // focus next tick
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // Clamp active index when filtered list shrinks
  useEffect(() => {
    if (activeIndex >= flatOrder.length) {
      setActiveIndex(Math.max(0, flatOrder.length - 1));
    }
  }, [activeIndex, flatOrder.length]);

  const handleSelect = (cmd: Command) => {
    onOpenChange(false);
    navigate(cmd.to);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flatOrder.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = flatOrder[activeIndex];
      if (cmd) handleSelect(cmd);
    } else if (e.key === 'Escape') {
      onOpenChange(false);
    }
  };

  const renderGroup = (group: Command['group'], offset: number) => {
    const items = grouped[group];
    if (items.length === 0) return null;
    return (
      <div key={group}>
        <div
          className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: 'var(--content-muted)' }}
        >
          {GROUP_LABEL[group]}
        </div>
        <ul role="listbox">
          {items.map((cmd, idx) => {
            const flatIdx = offset + idx;
            const isActive = flatIdx === activeIndex;
            const Icon = cmd.icon;
            return (
              <li
                key={cmd.id}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setActiveIndex(flatIdx)}
                onClick={() => handleSelect(cmd)}
                className="flex cursor-pointer items-center gap-2.5 px-3 py-2 rounded-md mx-1"
                style={{
                  background: isActive ? 'var(--surface-1)' : 'transparent',
                }}
                data-testid={`cmd-${cmd.id}`}
              >
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                  style={{
                    background: isActive ? 'var(--surface-raised)' : 'var(--surface-1)',
                    color: 'var(--content-default)',
                  }}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
                    {cmd.label}
                  </p>
                  <p className="truncate text-[11px]" style={{ color: 'var(--content-muted)' }}>
                    {cmd.description}
                  </p>
                </div>
                {cmd.shortcut ? (
                  <kbd
                    className="hidden font-mono text-[10px] sm:inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5"
                    style={{
                      background: 'var(--surface-raised)',
                      color: 'var(--content-muted)',
                      border: '1px solid var(--divider-strong)',
                    }}
                  >
                    {cmd.shortcut}
                  </kbd>
                ) : null}
                {isActive ? (
                  <CornerDownLeft
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: 'var(--content-muted)' }}
                    aria-hidden="true"
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ background: 'var(--scrim-modal)' }}
        />
        <DialogPrimitive.Content
          aria-label="명령어 팔레트"
          className="fixed left-1/2 top-[20%] z-50 w-[calc(100%-2rem)] max-w-[560px] -translate-x-1/2 overflow-hidden rounded-[var(--radius-lg)] shadow-[var(--elevation-3)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--divider-strong)',
          }}
          data-testid="command-palette"
        >
          <DialogPrimitive.Title className="sr-only">명령어 팔레트</DialogPrimitive.Title>
          {/* Search input */}
          <div
            className="flex items-center gap-2 px-4 py-3"
            style={{ borderBottom: '1px solid var(--divider-soft)' }}
          >
            <Search className="h-4 w-4 shrink-0" style={{ color: 'var(--content-muted)' }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="페이지, 작업 검색…"
              className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-[color:var(--content-muted)]"
              style={{ color: 'var(--content-strong)' }}
              data-testid="command-palette-input"
            />
            <kbd
              className="font-mono text-[10px] inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5"
              style={{
                background: 'var(--surface-1)',
                color: 'var(--content-muted)',
                border: '1px solid var(--divider-strong)',
              }}
            >
              ESC
            </kbd>
          </div>

          {/* List */}
          <div className="max-h-[60vh] overflow-y-auto py-1">
            {flatOrder.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-3 py-10">
                <Search className="h-5 w-5" style={{ color: 'var(--content-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
                  결과 없음
                </p>
              </div>
            ) : (
              <>
                {renderGroup('nav', 0)}
                {renderGroup('create', grouped.nav.length)}
                {renderGroup('action', grouped.nav.length + grouped.create.length)}
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between gap-3 px-4 py-2 text-[11px]"
            style={{
              borderTop: '1px solid var(--divider-soft)',
              background: 'var(--surface-1)',
              color: 'var(--content-muted)',
            }}
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1">
                <kbd className="font-mono inline-flex h-4 w-4 items-center justify-center rounded" style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}>↑</kbd>
                <kbd className="font-mono inline-flex h-4 w-4 items-center justify-center rounded" style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}>↓</kbd>
                탐색
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="font-mono inline-flex h-4 items-center justify-center rounded px-1" style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}>↵</kbd>
                선택
              </span>
            </div>
            <span>{flatOrder.length}개 결과</span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
